/**
 * Facet Configuration Editor
 * Allows admins to customize facet categories and type mappings per account
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useFacetConfig } from '@/hooks/useFacetConfig'
import { useSessionStore } from '@/stores/sessionStore'
import { facetConfigApi } from '@/lib/api/client'
import {
  Settings2,
  ChevronDown,
  ChevronRight,
  Pencil,
  RotateCcw,
  Save,
  Loader2,
  Check,
  X,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react'
import { type FacetCategory } from '@/types/interviewer'

// ============================================================================
// TYPES
// ============================================================================

interface FacetTypeEditState {
  facetTypeId: string
  label: string
  categoryId: string
  createsEntity: boolean
  defaultEntityType: string | null
  color: string | null
  sortOrder: number
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FacetConfigEditor() {
  const { accountId, projectId } = useSessionStore()
  const {
    categoryConfig,
    facetTypeConfig,
    mappings,
    loading,
    error,
    isDefault,
    resetConfig,
    refresh,
  } = useFacetConfig()

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['market', 'business', 'solution', 'insights']))
  const [editingFacet, setEditingFacet] = useState<FacetTypeEditState | null>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Group facet types by category
  const facetsByCategory = useMemo(() => {
    const grouped: Record<string, Array<{ id: string; config: typeof facetTypeConfig[string] }>> = {}

    Object.entries(facetTypeConfig).forEach(([id, config]) => {
      const category = config.category
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push({ id, config })
    })

    return grouped
  }, [facetTypeConfig])

  // Sort categories by sortOrder
  const sortedCategories = useMemo(() => {
    return Object.values(categoryConfig).sort((a, b) => a.sortOrder - b.sortOrder)
  }, [categoryConfig])

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const handleReset = async () => {
    setIsResetting(true)
    setSaveError(null)
    try {
      await resetConfig()
      setShowResetConfirm(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reset')
    } finally {
      setIsResetting(false)
    }
  }

  const handleSaveFacet = async () => {
    if (!editingFacet || !accountId || !projectId) return

    setIsSaving(true)
    setSaveError(null)

    try {
      // Build the full mappings array with the updated facet
      const allMappings = Object.values(mappings).map((m) => {
        if (m.facetTypeId === editingFacet.facetTypeId) {
          return {
            facetTypeId: editingFacet.facetTypeId,
            categoryId: editingFacet.categoryId,
            label: editingFacet.label,
            createsEntity: editingFacet.createsEntity,
            defaultEntityType: editingFacet.defaultEntityType || undefined,
            color: editingFacet.color || undefined,
            sortOrder: editingFacet.sortOrder,
          }
        }
        return {
          facetTypeId: m.facetTypeId,
          categoryId: m.categoryId,
          label: m.label,
          createsEntity: m.createsEntity,
          defaultEntityType: m.defaultEntityType || undefined,
          color: m.color || undefined,
          sortOrder: m.sortOrder,
        }
      })

      await facetConfigApi.updateMappings(accountId, projectId, allMappings)
      await refresh()
      setEditingFacet(null)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  if (!projectId) {
    return (
      <Card className="w-full">
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center gap-3 text-text-muted">
            <AlertTriangle className="w-8 h-8 text-solution" />
            <p className="text-center">Please select a project to configure facet types</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading facet configuration...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Status Messages */}
      {(error || saveError) && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm bg-solution/10 text-solution border border-solution/20">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error || saveError}</span>
        </div>
      )}

      {saveSuccess && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm bg-business/10 text-business border border-business/20">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>Configuration saved successfully</span>
        </div>
      )}

      {/* Header Card */}
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center flex-shrink-0">
                <Settings2 className="w-5 h-5 text-market" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base font-semibold">Facet Type Mappings</CardTitle>
                <CardDescription className="text-text-muted">
                  Configure which entity type each facet creates
                </CardDescription>
              </div>
            </div>
            {isDefault ? (
              <Badge variant="outline" className="flex-shrink-0">Using Defaults</Badge>
            ) : (
              <Badge className="bg-market/10 text-market border border-market/20 flex-shrink-0">Customized</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-text-muted">
            Click on any facet type to edit its label, category, or entity mapping.
            Changes are saved per account and apply to all users.
          </p>
        </CardContent>
      </Card>

      {/* Category List */}
      {sortedCategories.map(category => {
        const isExpanded = expandedCategories.has(category.id)
        const facets = facetsByCategory[category.id] || []
        const entityFacets = facets.filter(f => f.config.createsEntity)

        return (
          <Card key={category.id} className="w-full overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-warm-50 hover:bg-warm-100 transition-colors border-b border-border-subtle"
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: category.color }}
              />
              <span className="font-medium text-text flex-1 text-left">{category.label}</span>
              <span className="text-xs text-text-muted mr-2">
                {facets.length} facets · {entityFacets.length} create entities
              </span>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-text-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-text-muted" />
              )}
            </button>

            {/* Facet List */}
            {isExpanded && (
              <div className="divide-y divide-border-subtle">
                {facets.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-text-muted">
                    No facets in this category
                  </div>
                ) : (
                  facets.map(({ id, config }) => {
                    const mapping = mappings[id]
                    return (
                      <FacetTypeRow
                        key={id}
                        facetTypeId={id}
                        config={config}
                        isEditing={editingFacet?.facetTypeId === id}
                        isSaving={isSaving && editingFacet?.facetTypeId === id}
                        onEdit={() => setEditingFacet({
                          facetTypeId: id,
                          label: config.label,
                          categoryId: config.category,
                          createsEntity: config.createsEntity,
                          defaultEntityType: config.defaultEntityType,
                          color: config.color,
                          sortOrder: mapping?.sortOrder || 0,
                        })}
                        onCancel={() => setEditingFacet(null)}
                        onSave={handleSaveFacet}
                        editState={editingFacet?.facetTypeId === id ? editingFacet : null}
                        onEditChange={(changes) => {
                          if (editingFacet) {
                            setEditingFacet({ ...editingFacet, ...changes })
                          }
                        }}
                        categories={sortedCategories}
                      />
                    )
                  })
                )}
              </div>
            )}
          </Card>
        )
      })}

      {/* Actions Card */}
      <Card className="w-full">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-text-muted">
              Reset will restore all facet mappings to system defaults
            </p>
            <div className="flex gap-2">
              {showResetConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-muted">Reset to defaults?</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowResetConfirm(false)}
                    disabled={isResetting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    disabled={isResetting}
                    className="text-solution hover:text-solution hover:border-solution"
                  >
                    {isResetting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    Reset
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetConfirm(true)}
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Defaults
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================================================
// FACET TYPE ROW
// ============================================================================

interface FacetTypeRowProps {
  facetTypeId: string
  config: {
    category: FacetCategory
    label: string
    color: string
    icon: string
    createsEntity: boolean
    defaultEntityType: string | null
    allowedEntityTypes: string[] | null
  }
  isEditing: boolean
  isSaving: boolean
  editState: FacetTypeEditState | null
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  onEditChange: (changes: Partial<FacetTypeEditState>) => void
  categories: Array<{ id: string; label: string; color: string }>
}

function FacetTypeRow({
  config,
  isEditing,
  isSaving,
  editState,
  onEdit,
  onCancel,
  onSave,
  onEditChange,
  categories,
}: FacetTypeRowProps) {
  if (isEditing && editState) {
    return (
      <div className="px-4 py-4 bg-warm-50/50">
        <div className="space-y-4">
          {/* Facet ID (readonly) */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted w-24 flex-shrink-0">ID:</label>
            <code className="text-xs font-mono bg-warm-100 px-2 py-1 rounded text-text-secondary">
              {editState.facetTypeId}
            </code>
          </div>

          {/* Label */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted w-24 flex-shrink-0">Label:</label>
            <input
              type="text"
              value={editState.label}
              onChange={(e) => onEditChange({ label: e.target.value })}
              className="flex-1 px-3 py-1.5 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-market/50"
            />
          </div>

          {/* Category */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted w-24 flex-shrink-0">Category:</label>
            <select
              value={editState.categoryId}
              onChange={(e) => onEditChange({ categoryId: e.target.value })}
              className="flex-1 px-3 py-1.5 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-market/50"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Creates Entity Toggle */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted w-24 flex-shrink-0">Entity:</label>
            <button
              onClick={() => onEditChange({
                createsEntity: !editState.createsEntity,
                defaultEntityType: !editState.createsEntity ? editState.label : null
              })}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                editState.createsEntity
                  ? 'bg-market/10 text-market border border-market/30'
                  : 'bg-warm-100 text-text-muted border border-border-subtle'
              }`}
            >
              {editState.createsEntity ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Creates Entity
                </>
              ) : (
                <>
                  <X className="w-3.5 h-3.5" />
                  Insight Only
                </>
              )}
            </button>
          </div>

          {/* Default Entity Type */}
          {editState.createsEntity && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-text-muted w-24 flex-shrink-0">Maps to:</label>
              <input
                type="text"
                value={editState.defaultEntityType || ''}
                onChange={(e) => onEditChange({ defaultEntityType: e.target.value || null })}
                placeholder="e.g., Problem, Decision, Risk"
                className="flex-1 px-3 py-1.5 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-market/50 font-mono"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={onEdit}
      className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-warm-50/50 transition-colors text-left group"
    >
      {/* Color Indicator */}
      <div
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: config.color }}
      />

      {/* Label */}
      <span className="text-sm text-text flex-1">{config.label}</span>

      {/* Entity Mapping */}
      {config.createsEntity ? (
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <ArrowRight className="w-3 h-3" />
          <span className="font-mono bg-warm-100 px-1.5 py-0.5 rounded">
            {config.defaultEntityType}
          </span>
        </div>
      ) : (
        <span className="text-xs text-text-muted italic">insight only</span>
      )}

      {/* Edit Hint */}
      <Pencil className="w-3.5 h-3.5 text-text-muted/50 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

// ============================================================================
// COLOR PICKER (for future use - currently unused)
// ============================================================================

// const PRESET_COLORS = [
//   '#5c8a6b', // market green
//   '#4a90a4', // business blue
//   '#c9a227', // solution gold
//   '#6c757d', // insights gray
//   '#dc3545', // red
//   '#8b5cf6', // purple
//   '#22c55e', // green
//   '#f59e0b', // amber
// ]

// interface ColorPickerProps {
//   value: string
//   onChange: (color: string) => void
// }

// function ColorPicker({ value, onChange }: ColorPickerProps) {
//   const [isOpen, setIsOpen] = useState(false)

//   return (
//     <div className="relative">
//       <button
//         onClick={() => setIsOpen(!isOpen)}
//         className="flex items-center gap-2 px-2 py-1 border border-border-subtle rounded hover:border-warm-300 transition-colors"
//       >
//         <div
//           className="w-4 h-4 rounded"
//           style={{ backgroundColor: value }}
//         />
//         <Palette className="w-3.5 h-3.5 text-text-muted" />
//       </button>

//       {isOpen && (
//         <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-border-subtle rounded-lg shadow-lg z-10">
//           <div className="grid grid-cols-4 gap-1">
//             {PRESET_COLORS.map(color => (
//               <button
//                 key={color}
//                 onClick={() => {
//                   onChange(color)
//                   setIsOpen(false)
//                 }}
//                 className={`w-6 h-6 rounded transition-transform hover:scale-110 ${
//                   value === color ? 'ring-2 ring-offset-1 ring-market' : ''
//                 }`}
//                 style={{ backgroundColor: color }}
//               />
//             ))}
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }
